/**
 * Graphique de performance pour le dashboard de monitoring
 * Composant de visualisation des métriques avec Chart.js
 * @author Cascade (Tech Lead)
 * @date 2025-07-09
 */

import React, { useMemo } from 'react';
import type { PerformanceChartProps, ChartDataPoint } from '../../../types/monitoring.types';

const PerformanceChart: React.FC<PerformanceChartProps> = ({
  data,
  title,
  type = 'line',
  color = '#3B82F6',
  height = 300,
  isLoading = false,
  className = ''
}) => {
  // Calcul des statistiques pour les données
  const stats = useMemo(() => {
    if (!data || data.length === 0) return null;

    const values = data.map(point => point.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    
    return { min, max, avg };
  }, [data]);

  // Composant de loading
  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
          <div 
            className="bg-gray-300 rounded"
            style={{ height: `${height}px` }}
          ></div>
          <div className="flex justify-between mt-4">
            <div className="h-4 bg-gray-300 rounded w-20"></div>
            <div className="h-4 bg-gray-300 rounded w-20"></div>
            <div className="h-4 bg-gray-300 rounded w-20"></div>
          </div>
        </div>
      </div>
    );
  }

  // Si pas de données
  if (!data || data.length === 0) {
    return (
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div 
          className="flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300"
          style={{ height: `${height}px` }}
        >
          <div className="text-center">
            <div className="text-gray-400 text-4xl mb-2">📊</div>
            <p className="text-gray-500 text-sm">Aucune donnée disponible</p>
          </div>
        </div>
      </div>
    );
  }

  // Génération des points pour le graphique SVG simple
  const generatePath = (): string => {
    if (!stats) return '';

    const padding = 40;
    const chartWidth = 600 - (padding * 2);
    const chartHeight = height - (padding * 2);
    
    const xStep = chartWidth / (data.length - 1);
    const yRange = stats.max - stats.min || 1;
    
    const points = data.map((point, index) => {
      const x = padding + (index * xStep);
      const y = padding + chartHeight - ((point.value - stats.min) / yRange * chartHeight);
      return `${x},${y}`;
    });

    if (type === 'line') {
      return `M ${points.join(' L ')}`;
    } else if (type === 'area') {
      const firstPoint = points[0].split(',');
      const lastPoint = points[points.length - 1].split(',');
      return `M ${firstPoint[0]},${padding + chartHeight} L ${points.join(' L ')} L ${lastPoint[0]},${padding + chartHeight} Z`;
    }
    
    return '';
  };

  // Génération des barres pour un graphique en barres
  const generateBars = () => {
    if (!stats || type !== 'bar') return [];

    const padding = 40;
    const chartWidth = 600 - (padding * 2);
    const chartHeight = height - (padding * 2);
    
    const barWidth = chartWidth / data.length * 0.8;
    const barSpacing = chartWidth / data.length * 0.2;
    const yRange = stats.max - stats.min || 1;
    
    return data.map((point, index) => {
      const x = padding + (index * (barWidth + barSpacing)) + barSpacing / 2;
      const barHeight = (point.value - stats.min) / yRange * chartHeight;
      const y = padding + chartHeight - barHeight;
      
      return (
        <rect
          key={index}
          x={x}
          y={y}
          width={barWidth}
          height={barHeight}
          fill={color}
          opacity={0.7}
          className="hover:opacity-100 transition-opacity duration-200"
        />
      );
    });
  };

  return (
    <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {stats && (
          <div className="flex space-x-4 text-sm text-gray-600">
            <span>Min: {stats.min.toFixed(1)}</span>
            <span>Moy: {stats.avg.toFixed(1)}</span>
            <span>Max: {stats.max.toFixed(1)}</span>
          </div>
        )}
      </div>

      {/* Graphique SVG */}
      <div className="relative">
        <svg 
          width="100%" 
          height={height}
          viewBox={`0 0 600 ${height}`}
          className="overflow-visible"
        >
          {/* Grille */}
          <defs>
            <pattern id="grid" width="60" height="30" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 30" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Axes */}
          <line x1="40" y1="40" x2="40" y2={height - 40} stroke="#e5e7eb" strokeWidth="2" />
          <line x1="40" y1={height - 40} x2="560" y2={height - 40} stroke="#e5e7eb" strokeWidth="2" />

          {/* Données */}
          {type === 'bar' ? (
            generateBars()
          ) : (
            <>
              {type === 'area' && (
                <path
                  d={generatePath()}
                  fill={color}
                  fillOpacity={0.3}
                  stroke="none"
                />
              )}
              <path
                d={generatePath()}
                fill="none"
                stroke={color}
                strokeWidth={type === 'line' ? 3 : 2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Points */}
              {type === 'line' && data.map((point, index) => {
                const padding = 40;
                const chartWidth = 600 - (padding * 2);
                const chartHeight = height - (padding * 2);
                const xStep = chartWidth / (data.length - 1);
                const yRange = (stats?.max ?? 0) - (stats?.min ?? 0) || 1;
                
                const x = padding + (index * xStep);
                const y = padding + chartHeight - ((point.value - (stats?.min ?? 0)) / yRange * chartHeight);
                
                return (
                  <circle
                    key={index}
                    cx={x}
                    cy={y}
                    r="4"
                    fill={color}
                    className="hover:r-6 transition-all duration-200"
                  >
                    <title>{`${point.label}: ${point.value}`}</title>
                  </circle>
                );
              })}
            </>
          )}
        </svg>

        {/* Labels des axes */}
        <div className="flex justify-between mt-2 px-10 text-xs text-gray-500">
          {data.slice(0, 5).map((point, index) => (
            <span key={index} className="text-center">
              {point.label}
            </span>
          ))}
        </div>
      </div>

      {/* Footer avec informations supplémentaires */}
      {stats && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: color }}
                ></div>
                <span className="text-gray-600">{title}</span>
              </div>
            </div>
            <div className="text-gray-500">
              {data.length} point{data.length > 1 ? 's' : ''} de données
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceChart;
